import {useEffect, useRef} from 'react';

import {useDocumentContext} from '@/components/document/context/use-document-context';
import {useDocumentStore} from '@/components/document/store';
import {useGetDetailedInvestorAccountMutation} from '@/components/new-document-creation/hooks/use-get-investor-account-mutation';
import {INVE_FIELDS} from '@/constants/inve';
import {PRV_COMP_ARRAYS, PRV_COMP_FIELDS} from '@/constants/prv-comp';
import {isReferenceToSourceAccount, isReferenceToSourceAccountMeta} from '@/engine/utils/reference';
import {logger} from '@/initializers/logger';
import {BATCH_OPERATIONS, BatchUpdate} from '@/types/document/store';
import {mapAccountToCOMP} from '@/utils/client-info';

export const SOURCE_ACCOUNT_FIELD_ID = 'I211';

export const usePrefilledSourceInvestorAccount = (): void => {
  const {store, valueResolver} = useDocumentContext();
  const investorNo = useDocumentStore(state => state.data.INVE0);
  const sourceAccountIndex = useDocumentStore(state => state.data[SOURCE_ACCOUNT_FIELD_ID]);
  const batchUpdate = useDocumentStore(state => state.batchUpdate);
  const {mutate: mutateGetDetailedInvestorAccount} = useGetDetailedInvestorAccountMutation();

  // Avoids fetching the account on initialization, which is already done by useRefreshPrefilledTransaction.
  const isInitialized = useRef(false);
  // Avoids refetching the same account on every unrelated store update.
  const lastFetchedAccountNo = useRef<string | null>(null);

  useEffect(() => {
    const index = sourceAccountIndex ? valueResolver(sourceAccountIndex) : '';
    const accountNo =
      index === '' ? undefined : valueResolver({$ref: `${INVE_FIELDS.INVE43_existingAccountNo}#${index}`});
    const investorId = typeof investorNo === 'string' ? investorNo : undefined;

    if (!isInitialized.current || accountNo === lastFetchedAccountNo.current) {
      isInitialized.current = true;
      lastFetchedAccountNo.current = accountNo ?? null;
      return;
    }

    lastFetchedAccountNo.current = accountNo ?? null;

    const clearStaleKeys = () => {
      const staleKeys = Object.keys(store.getState().data).filter(
        key => isReferenceToSourceAccount(key) || isReferenceToSourceAccountMeta(key),
      );
      if (staleKeys.length) {
        batchUpdate(staleKeys.map<BatchUpdate>(id => ({type: BATCH_OPERATIONS.UNSET, id})));
      }
    };
    clearStaleKeys();

    if (!accountNo || !investorId) return;

    mutateGetDetailedInvestorAccount(
      {investorNo: investorId, accountNo: String(accountNo)},
      {
        onSuccess: account => {
          const mapped = mapAccountToCOMP(account, PRV_COMP_FIELDS, PRV_COMP_ARRAYS);

          batchUpdate(
            Object.entries(mapped).map<BatchUpdate>(([id, value]) => ({
              type: BATCH_OPERATIONS.SET,
              id,
              value,
            })),
          );
        },
        onError: error => {
          logger.error('Error fetching source account details:', error);
        },
      },
    );
  }, [sourceAccountIndex, investorNo, batchUpdate, store, valueResolver, mutateGetDetailedInvestorAccount]);
};
